// Map maker layouts: what the DM draws on the map grid before the AI paints it. The layout is the rulebook (walls,
// cover, difficult terrain); the generated image only has to look like it. Shared by the editor, the server (which
// validates and stores drafts) and the rules.
//
// Coordinates: column x runs left to right, row y top to bottom, both from 0. Grid corners run 0..columns and 0..rows.
// A wall edge "x,y,h" runs along the top of cell (x,y) from corner (x,y) to (x+1,y); "x,y,v" runs down its left side
// from corner (x,y) to (x,y+1). The right and bottom borders are the edges of the cells beyond them.

export const MAP_LAYOUT_VERSION = 1;

// Grid shapes for generated maps, with 5-foot squares. The image generator makes 1024x1024 images, so all are square.
export const MAP_SHAPES = Object.freeze({
  'square-16': Object.freeze({ label: 'Small · 16 × 16 (80 ft)', columns: 16, rows: 16, imageSize: '1024x1024' }),
  'square-24': Object.freeze({ label: 'Medium · 24 × 24 (120 ft)', columns: 24, rows: 24, imageSize: '1024x1024' }),
  'square-32': Object.freeze({ label: 'Large · 32 × 32 (160 ft)', columns: 32, rows: 32, imageSize: '1024x1024' }),
});
export const DEFAULT_MAP_SHAPE = 'square-24';

// Time of day for generated maps, and how the generator is told about it. Night stays readable: dim, never black.
export const MAP_LIGHTING = Object.freeze({
  day: Object.freeze({ label: 'Day', prompt: 'Lighting: daytime. Natural daylight with soft, consistent shadows; every part of the map clearly readable.' }),
  // Placed fires and torches light a layered map themselves, so its ground has no light of its own to confuse them with.
  night: Object.freeze({ label: 'Night', prompt: 'Lighting: night. Moonlit darkness in cool blue-grey tones. Light sources that fit the scene (torches, lanterns, campfires, lit windows) cast warm pools of light. Keep walls, paths and the playable ground readable: dim, never black.',
    groundPrompt: 'Lighting: night. Even moonlit darkness in cool blue-grey tones across the whole map, with no light sources and no warm or bright patches: the app adds those itself. Keep the ground readable: dim, never black.' }),
});
export const DEFAULT_MAP_LIGHTING = 'day';
// Drafts saved with the retired wide and tall shapes open as the smallest square that holds them.
const RETIRED_SHAPES = Object.freeze({ 'wide-24': 'square-24', 'tall-24': 'square-24', 'wide-36': 'square-32', 'tall-36': 'square-32' });

const entries = (list) => Object.freeze(Object.fromEntries(list.map((entry) => [entry.id, Object.freeze(entry)])));

// Terrain painted per square. `prompt` is how the generator is told what the color means.
// Ground paints. `difficult` squares cost extra movement (2024 Difficult Terrain: mud, and deep water, which is
// swimming); `blocksMovement` squares can't be entered (lava, a chasm).
export const TERRAIN = entries([
  { id: 'grass', label: 'Grass', color: '#6aa84f', prompt: 'grass' },
  { id: 'dirt', label: 'Dirt', color: '#8b6b4a', prompt: 'bare dirt' },
  { id: 'road', label: 'Road / path', color: '#b9a37a', prompt: 'a worn road or path' },
  { id: 'cobblestone', label: 'Cobblestone', color: '#7d7461', prompt: 'cobblestone paving' },
  { id: 'stone-floor', label: 'Stone floor', color: '#a3a3a3', prompt: 'flagstone floor' },
  { id: 'wood-floor', label: 'Wood floor', color: '#b5835a', prompt: 'wooden plank floor' },
  { id: 'carpet', label: 'Carpet / rug', color: '#8e3b46', prompt: 'a carpet or rug' },
  // Roof: paint a building's footprint with it to show the building closed, from above; paint floors instead to show inside.
  // `placed` paints aren't the generator's: they are drawn over its ground, on their exact squares (see mapOverlay.js).
  { id: 'roof', label: 'Roof', color: '#be185d', placed: true, prompt: "a building's roof seen from above (shingles, thatch or slate to suit the scene), hiding the room underneath; keep the building's walls at the roof's edges" },
  { id: 'sand', label: 'Sand', color: '#e3cf8f', prompt: 'sand' },
  { id: 'snow', label: 'Snow', color: '#eef3f8', prompt: 'snow' },
  { id: 'ice', label: 'Ice', color: '#bfe3f2', prompt: 'ice' },
  { id: 'mud', label: 'Mud', color: '#5b4636', prompt: 'mud', difficult: true },
  { id: 'shallow-water', label: 'Shallow water', color: '#6fb3e8', prompt: 'shallow water' },
  // Deep water is swimming: an extra foot per foot, unless the creature has a Swim Speed (2024).
  { id: 'deep-water', label: 'Deep water', color: '#1f4e8c', prompt: 'deep water', difficult: true, swimming: true },
  { id: 'lava', label: 'Lava', color: '#e25822', prompt: 'lava', blocksMovement: true },
  { id: 'rock', label: 'Rough rock', color: '#5c5c5c', prompt: 'rough rock ground' },
  { id: 'void', label: 'Chasm / pit', color: '#141414', prompt: 'a deep chasm or pit', blocksMovement: true },
]);

// Wall edges along grid lines. Cover is what the wall gives a creature behind it (2024 rules).
export const WALL_KINDS = entries([
  { id: 'wall', label: 'Wall', color: '#1b1b1b', cover: 'total', blocksMovement: true, prompt: 'a solid wall' },
  { id: 'door', label: 'Door', color: '#9c5a1c', cover: 'total', blocksMovement: false, prompt: 'a door' },
  { id: 'window', label: 'Window', color: '#5fc3e4', cover: 'three-quarters', blocksMovement: true, prompt: 'a window' },
  { id: 'low-wall', label: 'Low wall', color: '#7a6a58', cover: 'half', blocksMovement: false, prompt: 'a low wall' },
]);

// The ways a stamp can point. Up the page is how the art is drawn, so it isn't stored.
export const MAP_FACINGS = Object.freeze({
  north: Object.freeze({ label: 'Up', arrow: '↑', dx: 0, dy: -1 }),
  east: Object.freeze({ label: 'Right', arrow: '→', dx: 1, dy: 0 }),
  south: Object.freeze({ label: 'Down', arrow: '↓', dx: 0, dy: 1 }),
  west: Object.freeze({ label: 'Left', arrow: '←', dx: -1, dy: 0 }),
});
export const DEFAULT_MAP_FACING = 'north';

// Things stamped on a square: one per square.
export const MAP_OBJECTS = entries([
  { id: 'crates', label: 'Crates / barrels', symbol: 'C', cover: 'half', prompt: 'crates and barrels' },
  { id: 'boulder', label: 'Boulder', symbol: 'B', cover: 'half', prompt: 'a boulder' },
  // Retired for the table stamps: no longer offered, but maps that have one keep it and its cover.
  { id: 'furniture', label: 'Table / furniture', symbol: 'T', cover: 'half', prompt: 'furniture', retired: true },
  // Its own green, and a whole tree in the prompt: an orange block asked for as "a trunk" came out as a stump or crates.
  { id: 'tree', label: 'Thick tree', symbol: '♣', cover: 'three-quarters', prompt: 'a whole standing tree seen from directly above, its crown of branches filling the square (never a stump, log, crate or barrel)', sketchColor: '#22c55e' },
  // Retired: no longer offered, but maps that have one keep it and its cover.
  { id: 'pillar', label: 'Pillar / column', symbol: 'P', cover: 'three-quarters', prompt: 'a stone pillar', retired: true },
  { id: 'solid', label: 'Solid block', symbol: '■', cover: 'total', blocksMovement: true, prompt: 'solid wall mass' },
  // Tents are 3 by 3, stamped in one click: eight squares of Three-Quarters Cover, which can be walked on, around a solid
  // center. An open tent is a different thing, in OPEN_TENTS.
  { id: 'tent-brown', label: 'Tent (brown)', symbol: 'A', cover: 'three-quarters', prompt: 'a brown tent', stampSpan: 3, stampCenter: 'tent-center' },
  { id: 'tent-canvas', label: 'Tent (canvas)', symbol: 'A', cover: 'three-quarters', prompt: 'a canvas tent', stampSpan: 3, stampCenter: 'tent-center' },
  { id: 'tent-red', label: 'Tent (red)', symbol: 'A', cover: 'three-quarters', prompt: 'a red tent', stampSpan: 3, stampCenter: 'tent-center' },
  { id: 'tent-center', label: 'Tent center', symbol: '■', cover: 'total', blocksMovement: true, prompt: 'the middle of a tent', part: true },
  { id: 'rubble', label: 'Rubble', symbol: '~', cover: null, difficult: true, prompt: 'rubble' },
  // A body on the ground is in the way, but too low to hide behind. Retired for the body sticker, which turns to any angle:
  // no longer offered, but maps that have one keep it as Difficult Terrain.
  { id: 'bodies', label: 'Body', symbol: '†', cover: null, difficult: true, prompt: 'a fallen body', retired: true },
  { id: 'brush', label: 'Brush / undergrowth', symbol: '"', cover: null, difficult: true, prompt: 'thick undergrowth', sketchColor: '#14b8a6' },
  // Each square keeps the way it goes up. That is only a look: stairs have no cover and don't change movement.
  { id: 'stairs', label: 'Stairs', symbol: '≡', cover: null, prompt: 'stairs', facings: 'Top of the stairs' },
  // These light the squares around them on a night map. That is only a look: no rule reads it.
  { id: 'campfire', label: 'Campfire', symbol: '*', cover: null, prompt: 'a campfire' },
  { id: 'torch', label: 'Torch', symbol: 'i', cover: null, prompt: 'a lit torch on a stake' },
  { id: 'wall-light', label: 'Wall light', symbol: 'j', cover: null, prompt: 'a lit lamp mounted on a wall' },
  // Two stamped squares make an empty table. A filled one is stamped 2 by 2 in one click.
  { id: 'table-empty', label: 'Empty table', symbol: 'e', cover: 'half', prompt: 'an empty table' },
  { id: 'table-filled', label: 'Filled table', symbol: 'f', cover: 'half', prompt: 'a table with chairs and things on it', stampSpan: 2 },
  // Two stamped squares make one bed. It's only a look: no cover, and movement isn't changed.
  { id: 'bed', label: 'Bed', symbol: 'b', cover: null, prompt: 'a bed' },
  { id: 'lever-wall', label: 'Wall lever', symbol: 'L', cover: null, prompt: 'a lever mounted on a wall' },
  { id: 'lever-floor', label: 'Floor lever', symbol: 'l', cover: null, prompt: 'a floor lever' },
  { id: 'chest', label: 'Chest', symbol: 'h', cover: 'half', prompt: 'a chest' },
  { id: 'safe', label: 'Safe', symbol: 's', cover: 'half', prompt: 'a safe' },
  { id: 'wall-safe', label: 'Wall safe', symbol: 'w', cover: null, prompt: 'a safe set into a wall' },
  { id: 'strong-box', label: 'Strong box', symbol: 'x', cover: null, prompt: 'a small strong box' },
  // These glow in their own color, day or night. That is only a look too.
  { id: 'magic-circle-blue', label: 'Magic circle (blue)', symbol: 'o', cover: null, prompt: 'a glowing blue magic circle on the ground', sizes: [1, 2, 3] },
  { id: 'magic-circle-green', label: 'Magic circle (green)', symbol: 'o', cover: null, prompt: 'a glowing green magic circle on the ground', sizes: [1, 2, 3] },
  { id: 'magic-circle-purple', label: 'Magic circle (purple)', symbol: 'o', cover: null, prompt: 'a glowing purple magic circle on the ground', sizes: [1, 2, 3] },
  { id: 'magic-circle-red', label: 'Magic circle (red)', symbol: 'o', cover: null, prompt: 'a glowing red magic circle on the ground', sizes: [1, 2, 3] },
  // Blood lies on the ground under other stamps. It's only a look. Footprints walk the way their squares are stamped.
  { id: 'blood-puddle', label: 'Blood puddle', symbol: 'r', cover: null, prompt: 'a puddle of blood', sizes: [1, 2] },
  { id: 'blood-footprints', label: 'Bloody footprints', symbol: 'v', cover: null, prompt: 'bloody footprints', facings: 'Footprints walk', trail: true },
  // Heavily Obscured (2024): sight can't pass into, out of or through these squares. No cover, no effect on movement.
  { id: 'fog', label: 'Heavy fog / smoke', symbol: 'F', cover: null, obscures: 'heavy', prompt: 'a bank of thick fog or smoke filling the area', sketchColor: '#94a3b8' },
  { id: 'foliage', label: 'Thick foliage', symbol: '♠', cover: null, obscures: 'heavy', prompt: 'dense, thick foliage (tall bushes and low branches) filling the area', sketchColor: '#166534' },
]);

// Tents with their roofs off, for a fight inside them. They lie under the stamps in `layout.openTents`, by their top left
// square, so a campfire, a bed or a torch can stand on any of their squares. They're clear ground: no rule reads them, and
// the door only sets where light gets in and out.
export const OPEN_TENTS = entries([
  { id: 'tent-brown-open', label: 'Open tent (brown)', symbol: 'a', cover: null, facings: 'Door faces' },
  { id: 'tent-canvas-open', label: 'Open tent (canvas)', symbol: 'a', cover: null, facings: 'Door faces' },
  { id: 'tent-red-open', label: 'Open tent (red)', symbol: 'a', cover: null, facings: 'Door faces' },
]);
export const OPEN_TENT_SPAN = 3;

// Stickers aren't tied to the squares: each sits anywhere on the map at any angle, by its middle (in squares) and its turn
// (whole degrees clockwise; 0 is as the art is drawn, front up), in `layout.stickers`. They're drawn live over the finished
// map, under the tokens (MapStickers), never painted into it. A sticker gives cover from its turned outline, and the
// squares mostly under it are Difficult Terrain: climbing over it. `art` picks its picture, from the catalog entry
// `pictures` names (its own id when it names none). Vehicle art is drawn to its footprint; a body's art is fitted (`fit`)
// into its one square, as the body stamp was. A body is in the way but too low to hide behind.
export const STICKERS = entries([
  { id: 'car', label: 'Car', symbol: 'V', cover: 'half', difficult: true, width: 2, length: 3 },
  { id: 'cart', label: 'Cart', symbol: 'V', cover: 'half', difficult: true, width: 2, length: 3 },
  { id: 'truck', label: 'Truck', symbol: 'V', cover: 'half', difficult: true, width: 2, length: 4 },
  { id: 'motorcycle', label: 'Motorcycle', symbol: 'V', cover: 'half', difficult: true, width: 1, length: 2 },
  { id: 'body', label: 'Body', symbol: '†', cover: null, difficult: true, width: 1, length: 1, pictures: 'bodies', fit: 'contain' },
]);

/** The art catalog entry a sticker's pictures come from. */
export const stickerArtId = (kind) => STICKERS[kind]?.pictures || kind;

export const COVER_LABELS = Object.freeze({ half: 'Half Cover', 'three-quarters': 'Three-Quarters Cover', total: 'Total Cover' });

export const LAYOUT_LIMITS = Object.freeze({ pins: 100, stickers: 100, noteLength: 500, sceneLength: 2000, nameLength: 120 });

export const cellKey = (x, y) => `${x},${y}`;
export const edgeKey = (x, y, dir) => `${x},${y},${dir}`;

const shapeFor = (shapeId) => MAP_SHAPES[shapeId] || MAP_SHAPES[DEFAULT_MAP_SHAPE];

/** A blank layout for a grid shape. */
export const emptyLayout = (shapeId = DEFAULT_MAP_SHAPE) => {
  const shape = MAP_SHAPES[shapeId] ? shapeId : DEFAULT_MAP_SHAPE;
  const { columns, rows } = MAP_SHAPES[shape];
  return { version: MAP_LAYOUT_VERSION, shape, columns, rows, scene: '', lighting: DEFAULT_MAP_LIGHTING, terrain: {}, walls: {}, wallStyles: {}, objects: {}, objectSizes: {}, objectFacings: {}, objectSteps: {}, openTents: {}, stickers: [], pins: [] };
};

const inBounds = (layout, x, y) => Number.isInteger(x) && Number.isInteger(y) && x >= 0 && y >= 0 && x < layout.columns && y < layout.rows;

/** Paint (or with `terrainId` null, clear) a set of cells. */
export const paintCells = (layout, cells, terrainId) => {
  const terrain = { ...layout.terrain };
  for (const { x, y } of cells) {
    if (!inBounds(layout, x, y)) continue;
    if (terrainId && TERRAIN[terrainId]) terrain[cellKey(x, y)] = terrainId; else delete terrain[cellKey(x, y)];
  }
  return { ...layout, terrain };
};

/** The cells a square brush of `size` covers around (x, y): 1 = that square, 2 = 2x2 from it, 3 = 3x3 around it. */
export const brushCells = (layout, x, y, size = 1) => {
  const width = Math.max(1, Math.min(5, Math.floor(size) || 1));
  const start = width % 2 ? -Math.floor(width / 2) : 0;
  const cells = [];
  for (let dy = 0; dy < width; dy += 1) for (let dx = 0; dx < width; dx += 1) {
    const cx = x + start + dx;
    const cy = y + start + dy;
    if (inBounds(layout, cx, cy)) cells.push({ x: cx, y: cy });
  }
  return cells;
};

/** Flood fill from (x, y): every square connected to it (up/down/left/right) with the same terrain, not crossing walls. */
export const fillCells = (layout, x, y) => {
  if (!inBounds(layout, x, y)) return [];
  const start = layout.terrain[cellKey(x, y)] || null;
  const seen = new Set([cellKey(x, y)]);
  const queue = [{ x, y }];
  const cells = [];
  const blocked = (key) => Boolean(layout.walls[key]);
  while (queue.length) {
    const cell = queue.shift();
    cells.push(cell);
    const steps = [
      [cell.x + 1, cell.y, edgeKey(cell.x + 1, cell.y, 'v')], [cell.x - 1, cell.y, edgeKey(cell.x, cell.y, 'v')],
      [cell.x, cell.y + 1, edgeKey(cell.x, cell.y + 1, 'h')], [cell.x, cell.y - 1, edgeKey(cell.x, cell.y, 'h')],
    ];
    for (const [nx, ny, wall] of steps) {
      const key = cellKey(nx, ny);
      if (seen.has(key) || !inBounds(layout, nx, ny) || blocked(wall) || (layout.terrain[key] || null) !== start) continue;
      seen.add(key);
      queue.push({ x: nx, y: ny });
    }
  }
  return cells;
};

/**
 * The unit edges along a straight line between two grid corners. A line that is neither horizontal nor vertical
 * follows the longer direction from the start corner (walls run along grid lines).
 */
export const edgesAlongLine = (layout, from, to) => {
  const clamp = (value, max) => Math.max(0, Math.min(max, Math.round(value)));
  const a = { x: clamp(from.x, layout.columns), y: clamp(from.y, layout.rows) };
  const b = { x: clamp(to.x, layout.columns), y: clamp(to.y, layout.rows) };
  const horizontal = Math.abs(b.x - a.x) >= Math.abs(b.y - a.y);
  const edges = [];
  if (horizontal) {
    const [x0, x1] = a.x <= b.x ? [a.x, b.x] : [b.x, a.x];
    for (let x = x0; x < x1; x += 1) if (x < layout.columns) edges.push(edgeKey(x, a.y, 'h'));
  } else {
    const [y0, y1] = a.y <= b.y ? [a.y, b.y] : [b.y, a.y];
    for (let y = y0; y < y1; y += 1) if (y < layout.rows) edges.push(edgeKey(a.x, y, 'v'));
  }
  return edges;
};

/**
 * Set (or with `kind` null, remove) wall edges. `style` names the wall set whose art draws them. It's only a look: rules
 * read `walls`, never the style.
 */
export const setWalls = (layout, edges, kind, style) => {
  const walls = { ...layout.walls };
  const wallStyles = { ...layout.wallStyles };
  for (const key of edges) {
    if (kind && WALL_KINDS[kind]) walls[key] = kind; else delete walls[key];
    if (kind && WALL_KINDS[kind] && style) wallStyles[key] = style; else delete wallStyles[key];
  }
  return { ...layout, walls, wallStyles };
};

const keepsFacing = (objectId, facing) => Boolean(MAP_OBJECTS[objectId]?.facings) && facing !== DEFAULT_MAP_FACING && Object.keys(MAP_FACINGS).includes(facing);

// Squares a stamp can continue to, corners included.
const AROUND_SQUARE = Object.freeze([[-1, -1], [0, -1], [1, -1], [-1, 0], [1, 0], [-1, 1], [0, 1], [1, 1]]);

/**
 * Where a trail goes on a square: `turn`, clockwise from up the page in radians, from the squares stamped before and after
 * it, so it runs through a straight, leans into a corner and follows a diagonal. `last` is the end of the trail, and `bend`
 * points to the inside of a turn of 90 degrees or more, or is null. A square on its own points the way it was stamped.
 */
export const trailStep = (layout, x, y) => {
  const steps = layout.objectSteps || {};
  const here = steps[cellKey(x, y)];
  const beside = AROUND_SQUARE.map(([dx, dy]) => ({ x: x + dx, y: y + dy, step: steps[cellKey(x + dx, y + dy)] }))
    .filter(({ x: a, y: b, step }) => step !== undefined && layout.objects[cellKey(a, b)] === layout.objects[cellKey(x, y)]);
  const earlier = beside.filter(({ step }) => step < here).sort((a, b) => b.step - a.step)[0];
  const later = beside.filter(({ step }) => step > here).sort((a, b) => a.step - b.step)[0];
  const [from, to] = [earlier || { x, y }, later || { x, y }];
  const facing = MAP_FACINGS[objectFacing(layout, x, y)];
  const [dx, dy] = earlier || later ? [to.x - from.x, to.y - from.y] : [facing.dx, facing.dy];
  const [inX, inY, outX, outY] = earlier && later ? [x - earlier.x, y - earlier.y, later.x - x, later.y - y] : [1, 0, 1, 0];
  const [bendX, bendY] = inX * outX + inY * outY <= 0 ? [outX - inX, outY - inY] : [0, 0];
  const length = Math.hypot(bendX, bendY);
  return { turn: Math.atan2(dx, -dy), last: Boolean(earlier && !later), bend: length ? { dx: bendX / length, dy: bendY / length } : null };
};

/** The way a stamp on a square points. */
export const objectFacing = (layout, x, y) => layout.objectFacings?.[cellKey(x, y)] || DEFAULT_MAP_FACING;

/** Place (or with `objectId` null, remove) a stamp on a square. */
export const setObject = (layout, x, y, objectId, size = 1, facing = DEFAULT_MAP_FACING) => {
  if (!inBounds(layout, x, y)) return layout;
  const objects = { ...layout.objects };
  const objectSizes = { ...layout.objectSizes };
  const objectFacings = { ...layout.objectFacings };
  const objectSteps = { ...layout.objectSteps };
  if (objectId && MAP_OBJECTS[objectId]) objects[cellKey(x, y)] = objectId; else delete objects[cellKey(x, y)];
  // Only stamps that come in sizes keep one, and the plain size isn't stored. It's a look: no rule reads it.
  if (size > 1 && MAP_OBJECTS[objectId]?.sizes?.includes(size)) objectSizes[cellKey(x, y)] = size; else delete objectSizes[cellKey(x, y)];
  if (keepsFacing(objectId, facing)) objectFacings[cellKey(x, y)] = facing; else delete objectFacings[cellKey(x, y)];
  delete objectSteps[cellKey(x, y)];
  if (MAP_OBJECTS[objectId]?.trail) objectSteps[cellKey(x, y)] = Math.max(0, ...Object.values(objectSteps)) + 1;
  return { ...layout, objects, objectSizes, objectFacings, objectSteps };
};

// Whether an open tent with this top left square covers a square.
const tentCovers = (key, x, y) => {
  const [left, top] = key.split(',').map(Number);
  return x >= left && y >= top && x < left + OPEN_TENT_SPAN && y < top + OPEN_TENT_SPAN;
};

/**
 * What a click with an open tent does: the same tent is taken away, another is changed where it stands, and otherwise one
 * goes up from that square, kept on the map, in place of any it would overlap.
 */
export const toggleOpenTent = (layout, x, y, id, facing = DEFAULT_MAP_FACING) => {
  if (!inBounds(layout, x, y) || !OPEN_TENTS[id]) return layout;
  const openTents = { ...layout.openTents };
  const here = Object.keys(openTents).find((key) => tentCovers(key, x, y));
  if (here) {
    if (openTents[here].id === id && openTents[here].facing === facing) delete openTents[here];
    else openTents[here] = { id, facing };
    return { ...layout, openTents };
  }
  const [left, top] = [Math.min(x, layout.columns - OPEN_TENT_SPAN), Math.min(y, layout.rows - OPEN_TENT_SPAN)];
  const squares = Array.from({ length: OPEN_TENT_SPAN ** 2 }, (_, index) => [left + (index % OPEN_TENT_SPAN), top + Math.floor(index / OPEN_TENT_SPAN)]);
  for (const key of Object.keys(openTents)) if (squares.some(([a, b]) => tentCovers(key, a, b))) delete openTents[key];
  openTents[cellKey(left, top)] = { id, facing };
  return { ...layout, openTents };
};

const DEGREES = Math.PI / 180;
const wholeTurn = (turn) => ((Math.round(turn) % 360) + 360) % 360;
// Half its size across and down the page as it lies: the box that holds it.
const reach = ({ kind, turn }) => {
  const { width, length } = STICKERS[kind];
  const [cos, sin] = [Math.abs(Math.cos(turn * DEGREES)), Math.abs(Math.sin(turn * DEGREES))];
  const round = (value) => Math.round(value * 1e6) / 1e6;
  return { x: round((width * cos + length * sin) / 2), y: round((width * sin + length * cos) / 2) };
};

/** A point in a sticker's own frame: across it and along it from its middle, front up, in squares. */
export const stickerFrame = (sticker, point) => {
  const [dx, dy] = [point.x - sticker.x, point.y - sticker.y];
  const [cos, sin] = [Math.cos(sticker.turn * DEGREES), Math.sin(sticker.turn * DEGREES)];
  return { x: dx * cos + dy * sin, y: -dx * sin + dy * cos };
};

/** A sticker's four corners on the map, clockwise from its front left. */
export const stickerCorners = (sticker) => {
  const { width, length } = STICKERS[sticker.kind];
  const [cos, sin] = [Math.cos(sticker.turn * DEGREES), Math.sin(sticker.turn * DEGREES)];
  return [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([a, b]) => {
    const [x, y] = [(a * width) / 2, (b * length) / 2];
    return { x: sticker.x + x * cos - y * sin, y: sticker.y + x * sin + y * cos };
  });
};

const onSticker = (sticker, point) => {
  const { width, length } = STICKERS[sticker.kind];
  const local = stickerFrame(sticker, point);
  return Math.abs(local.x) < width / 2 && Math.abs(local.y) < length / 2;
};

/** The squares mostly under a sticker: those whose middle it covers. */
export const stickerSquares = (sticker) => {
  const around = reach(sticker);
  const squares = [];
  for (let y = Math.floor(sticker.y - around.y); y < sticker.y + around.y; y += 1) {
    for (let x = Math.floor(sticker.x - around.x); x < sticker.x + around.x; x += 1) {
      if (onSticker(sticker, { x: x + 0.5, y: y + 0.5 })) squares.push({ x, y });
    }
  }
  return squares;
};

/** The sticker a point is on, the one on top where they overlap, or null. */
export const stickerAt = (layout, point) => [...(layout.stickers || [])].reverse().find((sticker) => onSticker(sticker, point)) || null;

/**
 * Where a sticker of `kind` turned `turn` goes for a point: lying square to the grid, it lines up with the squares; at any
 * other angle its middle keeps to half squares. Either way it stays on the map.
 */
export const snapStickerCenter = (layout, kind, point, turn) => {
  const around = reach({ kind, turn: wholeTurn(turn) });
  const square = wholeTurn(turn) % 90 === 0;
  const snap = (value, half) => (square ? Math.round(value - half) + half : Math.round(value * 2) / 2);
  const keep = (value, half, size) => Math.min(Math.max(value, half), Math.max(half, size - half));
  return { x: keep(snap(point.x, around.x), around.x, layout.columns), y: keep(snap(point.y, around.y), around.y, layout.rows) };
};

// A sticker from untrusted input, or null: a known kind with its middle on the map.
const cleanSticker = (raw, { columns, rows }) => {
  if (!raw || typeof raw !== 'object' || !STICKERS[raw.kind]) return null;
  const [x, y, turn] = [raw.x, raw.y, raw.turn ?? 0].map((value) => (typeof value === 'number' ? value : Number.NaN));
  if (![x, y, turn].every(Number.isFinite) || x < 0 || y < 0 || x > columns || y > rows) return null;
  const id = cleanText(raw.id, 40);
  return id ? { id, kind: raw.kind, x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100, turn: wholeTurn(turn),
    art: Number.isInteger(raw.art) && raw.art >= 0 ? raw.art : 0 } : null;
};

/** A saved layout's sticker list as stored, under the name it was saved with: maps and drafts from before stickers had their name keep them as `vehicles`. */
export const savedStickers = (raw) => {
  const list = Array.isArray(raw?.stickers) ? raw.stickers : raw?.vehicles;
  return Array.isArray(list) ? list : [];
};

/** Add a sticker, unless the map holds as many as it can or one already has its id. */
export const placeSticker = (layout, sticker) => {
  const stickers = layout.stickers || [];
  const clean = cleanSticker(sticker, layout);
  if (!clean || stickers.length >= LAYOUT_LIMITS.stickers || stickers.some(({ id }) => id === clean.id)) return layout;
  return { ...layout, stickers: [...stickers, clean] };
};

/** Move, turn or repicture one sticker. */
export const updateSticker = (layout, id, patch) => ({ ...layout,
  stickers: (layout.stickers || []).map((sticker) => (sticker.id === id ? cleanSticker({ ...sticker, ...patch, id }, layout) || sticker : sticker)) });

export const removeSticker = (layout, id) => ({ ...layout, stickers: (layout.stickers || []).filter((sticker) => sticker.id !== id) });

/** Erase everything a square holds: terrain, stamp, the walls along its four sides, any open tent over it and any sticker whose middle is on it. */
export const eraseCells = (layout, cells) => {
  const terrain = { ...layout.terrain };
  const objects = { ...layout.objects };
  const objectSizes = { ...layout.objectSizes };
  const objectFacings = { ...layout.objectFacings };
  const objectSteps = { ...layout.objectSteps };
  const openTents = { ...layout.openTents };
  const walls = { ...layout.walls };
  const wallStyles = { ...layout.wallStyles };
  const erased = new Set(cells.map(({ x, y }) => cellKey(x, y)));
  const stickers = (layout.stickers || []).filter((sticker) => !erased.has(cellKey(Math.floor(sticker.x), Math.floor(sticker.y))));
  for (const { x, y } of cells) {
    delete terrain[cellKey(x, y)];
    delete objects[cellKey(x, y)];
    delete objectSizes[cellKey(x, y)];
    delete objectFacings[cellKey(x, y)];
    delete objectSteps[cellKey(x, y)];
    for (const key of Object.keys(openTents)) if (tentCovers(key, x, y)) delete openTents[key];
    for (const key of [edgeKey(x, y, 'h'), edgeKey(x, y, 'v'), edgeKey(x + 1, y, 'v'), edgeKey(x, y + 1, 'h')]) {
      delete walls[key];
      delete wallStyles[key];
    }
  }
  return { ...layout, terrain, objects, objectSizes, objectFacings, objectSteps, openTents, stickers, walls, wallStyles };
};

// Text without control characters (tabs and line breaks stay), trimmed and capped.
const cleanText = (value, max) => Array.from(String(value ?? ''))
  .filter((char) => { const code = char.charCodeAt(0); return code >= 32 || code === 9 || code === 10 || code === 13; })
  .join('').trim().slice(0, max);

/**
 * A layout from untrusted input, keeping only what is valid: a known shape, known terrain/wall/stamp ids inside the
 * grid, and at most LAYOUT_LIMITS.pins pins with trimmed notes. Throws only when the input is not an object.
 */
export const normalizeLayout = (raw) => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw Object.assign(new Error('A map layout is required.'), { status: 400 });
  const base = emptyLayout(MAP_SHAPES[raw.shape] ? raw.shape : RETIRED_SHAPES[raw.shape] || DEFAULT_MAP_SHAPE);
  const { columns, rows } = shapeFor(base.shape);
  const parseCell = (key) => {
    const match = /^(\d+),(\d+)$/.exec(key);
    return match ? { x: Number(match[1]), y: Number(match[2]) } : null;
  };
  const terrain = {};
  for (const [key, id] of Object.entries(raw.terrain && typeof raw.terrain === 'object' ? raw.terrain : {})) {
    const cell = parseCell(key);
    if (cell && cell.x < columns && cell.y < rows && TERRAIN[id]) terrain[cellKey(cell.x, cell.y)] = id;
  }
  const objects = {};
  for (const [key, id] of Object.entries(raw.objects && typeof raw.objects === 'object' ? raw.objects : {})) {
    const cell = parseCell(key);
    if (cell && cell.x < columns && cell.y < rows && MAP_OBJECTS[id]) objects[cellKey(cell.x, cell.y)] = id;
  }
  const objectSizes = {};
  for (const [key, size] of Object.entries(raw.objectSizes && typeof raw.objectSizes === 'object' ? raw.objectSizes : {})) {
    if (size > 1 && Number.isInteger(size) && MAP_OBJECTS[objects[key]]?.sizes?.includes(size)) objectSizes[key] = size;
  }
  const objectFacings = {};
  for (const [key, facing] of Object.entries(raw.objectFacings && typeof raw.objectFacings === 'object' ? raw.objectFacings : {})) {
    if (keepsFacing(objects[key], facing)) objectFacings[key] = facing;
  }
  const objectSteps = {};
  for (const [key, step] of Object.entries(raw.objectSteps && typeof raw.objectSteps === 'object' ? raw.objectSteps : {})) {
    if (Number.isInteger(step) && step > 0 && MAP_OBJECTS[objects[key]]?.trail) objectSteps[key] = step;
  }
  const openTents = {};
  for (const [key, tent] of Object.entries(raw.openTents && typeof raw.openTents === 'object' ? raw.openTents : {})) {
    const cell = parseCell(key);
    if (!cell || cell.x > columns - OPEN_TENT_SPAN || cell.y > rows - OPEN_TENT_SPAN || !Object.keys(OPEN_TENTS).includes(tent?.id)) continue;
    openTents[cellKey(cell.x, cell.y)] = { id: tent.id, facing: Object.keys(MAP_FACINGS).includes(tent.facing) ? tent.facing : DEFAULT_MAP_FACING };
  }
  const walls = {};
  for (const [key, kind] of Object.entries(raw.walls && typeof raw.walls === 'object' ? raw.walls : {})) {
    const match = /^(\d+),(\d+),(h|v)$/.exec(key);
    if (!match || !WALL_KINDS[kind]) continue;
    const x = Number(match[1]);
    const y = Number(match[2]);
    const valid = match[3] === 'h' ? x < columns && y <= rows : x <= columns && y < rows;
    if (valid) walls[edgeKey(x, y, match[3])] = kind;
  }
  const wallStyles = {};
  for (const [key, style] of Object.entries(raw.wallStyles && typeof raw.wallStyles === 'object' ? raw.wallStyles : {})) {
    const name = typeof style === 'string' ? cleanText(style, LAYOUT_LIMITS.nameLength) : '';
    if (name && walls[key]) wallStyles[key] = name;
  }
  const stickers = [];
  for (const entry of savedStickers(raw)) {
    const sticker = cleanSticker(entry, { columns, rows });
    if (sticker && stickers.length < LAYOUT_LIMITS.stickers && !stickers.some(({ id }) => id === sticker.id)) stickers.push(sticker);
  }
  const pins = (Array.isArray(raw.pins) ? raw.pins : []).slice(0, LAYOUT_LIMITS.pins).map((pin, index) => ({
    id: cleanText(pin?.id, 40) || `pin-${index + 1}`,
    x: Math.max(0, Math.min(columns, Number(pin?.x) || 0)),
    y: Math.max(0, Math.min(rows, Number(pin?.y) || 0)),
    note: cleanText(pin?.note, LAYOUT_LIMITS.noteLength),
  })).filter((pin, index, list) => list.findIndex((other) => other.id === pin.id) === index);
  return { ...base, scene: cleanText(raw.scene, LAYOUT_LIMITS.sceneLength), lighting: MAP_LIGHTING[raw.lighting] ? raw.lighting : DEFAULT_MAP_LIGHTING,
    terrain, walls, wallStyles, objects, objectSizes, objectFacings, objectSteps, openTents, stickers, pins };
};

// How a layout is drawn as the generator's sketch, and how the prompt names each mark (kept together so they match).
// Pins with a note are drawn as numbered white circles so the generator sees where each note belongs; the prompt tells
// it to paint what the note says, never the circle. Doors are thick lime bars, a color no paint or stamp uses.
export const SKETCH_STYLE = Object.freeze({
  unpainted: Object.freeze({ color: '#3c3c3c', prompt: 'dark grey squares are unspecified ground: choose what suits the scene' }),
  cover: Object.freeze({ half: '#facc15', 'three-quarters': '#fb923c', total: '#b91c1c', difficult: '#a78bfa', none: '#cbd5e1' }),
  door: '#a3e635',
  pin: Object.freeze({ fill: '#ffffff', ring: '#111111' }),
  walls: Object.freeze({
    wall: 'thick black lines are solid walls',
    door: 'thick lime-green bars set in the walls are doors',
    window: 'light blue lines are windows set in walls',
    'low-wall': 'thin grey-brown lines are waist-high low walls',
  }),
});

/** A stamp's color in the sketch: its own (brush, so it isn't mistaken for rubble; a tree, so it isn't taken for wood) or its cover category's. */
export const objectSketchColor = (id) => {
  const object = MAP_OBJECTS[id];
  if (!object) return SKETCH_STYLE.cover.none;
  return object.sketchColor || SKETCH_STYLE.cover[object.cover || (object.difficult ? 'difficult' : 'none')];
};

/**
 * Patches of touching squares (sharing a side) stamped with the same object, each one feature: one rubble field,
 * one stand of brush, one stack of crates. Each patch lists its squares and the columns and rows it spans.
 */
export const objectGroups = (layout) => {
  const seen = new Set();
  const groups = [];
  const entries = Object.entries(layout?.objects || {}).filter(([, id]) => MAP_OBJECTS[id])
    .map(([key, id]) => { const [x, y] = key.split(',').map(Number); return { x, y, id }; })
    .sort((a, b) => a.y - b.y || a.x - b.x);
  for (const start of entries) {
    if (seen.has(cellKey(start.x, start.y))) continue;
    const cells = [];
    const queue = [start];
    seen.add(cellKey(start.x, start.y));
    while (queue.length) {
      const cell = queue.shift();
      cells.push({ x: cell.x, y: cell.y });
      for (const [nx, ny] of [[cell.x + 1, cell.y], [cell.x - 1, cell.y], [cell.x, cell.y + 1], [cell.x, cell.y - 1]]) {
        const key = cellKey(nx, ny);
        if (seen.has(key) || layout.objects[key] !== start.id) continue;
        seen.add(key);
        queue.push({ x: nx, y: ny, id: start.id });
      }
    }
    const xs = cells.map((cell) => cell.x);
    const ys = cells.map((cell) => cell.y);
    groups.push({ id: start.id, cells, left: Math.min(...xs), right: Math.max(...xs), top: Math.min(...ys), bottom: Math.max(...ys) });
  }
  return groups;
};

const MAX_PROMPT_BUILDINGS = 30;
const span = (from, to, word) => (from === to ? `${word} ${from + 1}` : `${word}s ${from + 1}-${to + 1}`);
// 0-based positions as 1-based runs: [2, 3, 4, 7] -> "columns 3-5 and 8".
const runs = (positions, word) => {
  const sorted = [...new Set(positions)].sort((a, b) => a - b);
  const parts = [];
  for (let i = 0; i < sorted.length; i += 1) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j += 1;
    parts.push(i === j ? `${sorted[i] + 1}` : `${sorted[i] + 1}-${sorted[j] + 1}`);
    i = j;
  }
  return `${word}${sorted.length > 1 ? 's' : ''} ${parts.join(' and ')}`;
};

/** Where a point sits on the map in words: "near the top left", "in the middle", "along the bottom edge, center". */
export const describeMapArea = (layout, x, y) => {
  const third = (value, size) => (value < size / 3 ? 0 : value < (2 * size) / 3 ? 1 : 2);
  const across = ['left', 'center', 'right'][third(x, layout.columns)];
  const down = ['top', 'middle', 'bottom'][third(y, layout.rows)];
  if (down === 'middle' && across === 'center') return 'in the middle of the map';
  if (down === 'middle') return `on the ${across} side, halfway down`;
  return `near the ${down}${across === 'center' ? ', centered' : ` ${across}`}`;
};

/**
 * Buildings: patches of touching roof squares, split by any wall line between them (row houses sharing a wall are two
 * buildings). Each lists its squares, bounds and the doors and windows on its outer edge by side ("top" is the map's
 * top), as 0-based columns (top and bottom walls) or rows (left and right walls). Numbered in reading order.
 */
export const buildingGroups = (layout) => {
  const roofs = Object.entries(layout?.terrain || {}).filter(([, id]) => id === 'roof')
    .map(([key]) => { const [x, y] = key.split(',').map(Number); return { x, y }; })
    .sort((a, b) => a.y - b.y || a.x - b.x);
  const isRoof = new Set(roofs.map(({ x, y }) => cellKey(x, y)));
  const walls = layout?.walls || {};
  const seen = new Set();
  const buildings = [];
  for (const start of roofs) {
    if (seen.has(cellKey(start.x, start.y))) continue;
    const cells = [];
    const queue = [start];
    seen.add(cellKey(start.x, start.y));
    while (queue.length) {
      const cell = queue.shift();
      cells.push(cell);
      for (const [nx, ny, edge] of [[cell.x + 1, cell.y, edgeKey(cell.x + 1, cell.y, 'v')], [cell.x - 1, cell.y, edgeKey(cell.x, cell.y, 'v')],
        [cell.x, cell.y + 1, edgeKey(cell.x, cell.y + 1, 'h')], [cell.x, cell.y - 1, edgeKey(cell.x, cell.y, 'h')]]) {
        const key = cellKey(nx, ny);
        if (seen.has(key) || !isRoof.has(key) || walls[edge]) continue;
        seen.add(key);
        queue.push({ x: nx, y: ny });
      }
    }
    const inGroup = new Set(cells.map(({ x, y }) => cellKey(x, y)));
    const openings = { door: { top: [], bottom: [], left: [], right: [] }, window: { top: [], bottom: [], left: [], right: [] } };
    for (const { x, y } of cells) {
      for (const [side, edge, neighbor, at] of [['top', edgeKey(x, y, 'h'), cellKey(x, y - 1), x], ['bottom', edgeKey(x, y + 1, 'h'), cellKey(x, y + 1), x],
        ['left', edgeKey(x, y, 'v'), cellKey(x - 1, y), y], ['right', edgeKey(x + 1, y, 'v'), cellKey(x + 1, y), y]]) {
        if (!inGroup.has(neighbor) && openings[walls[edge]]) openings[walls[edge]][side].push(at);
      }
    }
    const xs = cells.map((cell) => cell.x);
    const ys = cells.map((cell) => cell.y);
    buildings.push({ number: buildings.length + 1, cells, left: Math.min(...xs), right: Math.max(...xs), top: Math.min(...ys), bottom: Math.max(...ys), openings });
  }
  return buildings;
};

const describeOpenings = (sides) => ['top', 'bottom', 'left', 'right'].filter((side) => sides[side].length)
  .map((side) => `${side} wall at ${runs(sides[side], side === 'top' || side === 'bottom' ? 'column' : 'row')}`).join(', ');

const describeBuilding = (building) => {
  const width = building.right - building.left + 1;
  const height = building.bottom - building.top + 1;
  const shape = width * height === building.cells.length ? `a ${width} by ${height} rectangle`
    : `${building.cells.length} squares, not a full rectangle: follow the sketch's outline`;
  const doors = describeOpenings(building.openings.door);
  const windows = describeOpenings(building.openings.window);
  return `- Building ${building.number}: roof over ${span(building.left, building.right, 'column')}, ${span(building.top, building.bottom, 'row')} (${shape}); `
    + `${doors ? `doors: ${doors}` : 'no doors'}${windows ? `; windows: ${windows}` : ''}.`;
};

// What surrounds a pin's square: the building it is on, or the ground and the nearest building within two squares.
const pinSurroundings = (layout, buildings, x, y) => {
  const inside = buildings.find((building) => building.cells.some((cell) => cell.x === x && cell.y === y));
  if (inside) return `on the roof of building ${inside.number}`;
  const ground = TERRAIN[layout.terrain[cellKey(x, y)]]?.prompt || 'open ground';
  const stamp = MAP_OBJECTS[layout.objects[cellKey(x, y)]];
  const on = `on ${ground}${stamp ? ` with ${stamp.prompt}` : ''}`;
  let nearest = null;
  for (const building of buildings) {
    const distance = Math.min(...building.cells.map((cell) => Math.max(Math.abs(cell.x - x), Math.abs(cell.y - y))));
    if (distance <= 2 && (!nearest || distance < nearest.distance)) nearest = { building, distance };
  }
  if (!nearest) return on;
  const { building, distance } = nearest;
  const vertical = y < building.top ? 'top' : y > building.bottom ? 'bottom' : null;
  const horizontal = x < building.left ? 'left' : x > building.right ? 'right' : null;
  const where = vertical && horizontal ? `off its ${vertical}-${horizontal} corner` : vertical || horizontal ? `beside its ${vertical || horizontal} wall`
    : 'in a notch of its outline, outside its walls';
  return `${on}, ${distance === 1 ? 'right next to' : 'one square from'} building ${building.number}, ${where}`;
};

// What each ground color in the sketch means: only those used, plus unpainted squares if there are any.
const groundLegend = (layout) => {
  const terrainUsed = [...new Set(Object.values(layout.terrain))].filter((id) => TERRAIN[id]);
  const unpainted = Object.keys(layout.terrain).length < layout.columns * layout.rows;
  if (!terrainUsed.length && !unpainted) return [];
  return ['Ground colors in the sketch:', ...terrainUsed.map((id) => `- ${TERRAIN[id].color}: ${TERRAIN[id].prompt}`),
    ...(unpainted ? [`- ${SKETCH_STYLE.unpainted.color}: ${SKETCH_STYLE.unpainted.prompt}`] : [])];
};

/**
 * What the generator paints on a layered map: the same layout with only its ground. Stamps, walls and `placed` paints
 * (roofs) go on top afterwards, so they land exactly on their squares; noted pins stay, as things to paint on the ground.
 */
export const groundLayout = (layout) => ({ ...layout, walls: {}, wallStyles: {}, objects: {}, objectSizes: {}, objectFacings: {}, objectSteps: {}, openTents: {}, stickers: [],
  terrain: Object.fromEntries(Object.entries(layout.terrain).filter(([, id]) => !TERRAIN[id]?.placed)) });

// A pin's square, numbered as in the editor. Pins without a note aren't drawn or listed.
const notedPins = (layout) => layout.pins.map((pin, index) => ({ pin, number: index + 1 })).filter(({ pin }) => pin.note)
  .map(({ pin, number }) => ({ pin, number, x: Math.min(layout.columns - 1, Math.floor(pin.x)), y: Math.min(layout.rows - 1, Math.floor(pin.y)) }));

/** The generator's instructions for painting only a layout's ground: the framing, the scene, the colors and the notes. */
export const describeGroundForPrompt = (layout) => {
  const ground = groundLayout(layout);
  const { columns, rows } = ground;
  const lines = [
    `The supplied image is a top-down sketch of this map's ground: ${columns} by ${rows} squares of 5 feet (${columns * 5} by ${rows * 5} feet). Paint the ground over it, matching it square for square.`,
    `Keep the sketch's exact framing: the edges of the sketch are the edges of the map. Do not zoom, crop, pad, rotate or shift anything. Each sketch square is exactly 1/${columns} of the width and 1/${rows} of the height, and a colored area keeps its exact outline.`,
    'Paint ONLY the ground surface. Nothing may stand on it or rise from it. The app places every object on top afterwards, exactly on its square, and players must be able to trust that everything they see is really there. So paint no trees, stumps, logs, branches, bushes, shrubs, ferns, tall grass, reeds, flowers, mushrooms, hedges, rocks, stones, boulders, rubble, crates, barrels, furniture, walls, fences, buildings, roofs, doors, tents, campfires, torches, lanterns or bodies.',
    'The only exception is something named outright in the Scene line or a numbered note, such as "a fallen log across the path": paint that, and nothing like it anywhere else. A setting or mood, such as a forest or ruins, only sets how the ground looks and asks for none of these.',
  ];
  if (ground.scene) lines.push(`Scene: ${ground.scene}`);
  const lighting = MAP_LIGHTING[ground.lighting] || MAP_LIGHTING[DEFAULT_MAP_LIGHTING];
  lines.push(lighting.groundPrompt || lighting.prompt, ...groundLegend(ground));
  const notes = notedPins(ground);
  if (notes.length) {
    lines.push('Numbered notes: a white circle with a number marks each spot below (columns count from the left and rows from the top, starting at 1).');
    notes.forEach(({ pin, number, x, y }) => lines.push(`- Circle ${number}, column ${x + 1}, row ${y + 1} (${describeMapArea(ground, pin.x, pin.y)}; ${pinSurroundings(ground, [], x, y)}): ${pin.note}`));
    lines.push('Each note goes on its circled square, at the size it needs, and nowhere else. Paint what each note says, never the circle or its number.');
  }
  lines.push('Keep the surface flat and plain so it reads as open ground: short grass, bare soil, cracks, puddles, fallen leaves, thin snow and worn paving are fine. Nothing taller than a boot, nothing that casts its own shadow, and no clumps or scattered stones that could pass for an object.');
  lines.push('Remove every sketch color block, circle and number from the final art; they only describe the layout. Do not draw markers, pins, numbers or labels anywhere.');
  return lines.join('\n');
};

/** What a painted ground depends on. A saved ground fits a layout with the same signature, whatever sits on top of it. */
export const groundSignature = (layout) => {
  const ground = groundLayout(layout);
  return JSON.stringify([ground.shape, ground.scene, ground.lighting, Object.entries(ground.terrain).sort(([a], [b]) => a.localeCompare(b)),
    notedPins(ground).map(({ pin, x, y }) => [x, y, pin.note])]);
};

/**
 * The generator's instructions for a layout: the framing, the scene, what each sketch color and mark means (only those
 * used), each building with its doors and windows, the numbered notes with what surrounds them, and the rule that
 * unmarked squares stay open.
 */
export const describeLayoutForPrompt = (layout) => {
  const { columns, rows } = layout;
  const lines = [
    `The supplied image is a top-down layout sketch of this map: ${columns} by ${rows} squares of 5 feet (${columns * 5} by ${rows * 5} feet). Paint the finished map over it, matching it square for square.`,
    `Keep the sketch's exact framing: the edges of the sketch are the edges of the map. Do not zoom, crop, pad, rotate or shift anything. Each sketch square is exactly 1/${columns} of the width and 1/${rows} of the height, and everything stays in the same squares: a wall drawn along a square edge stays on that edge, and a colored area keeps its exact outline.`,
  ];
  if (layout.scene) lines.push(`Scene: ${layout.scene}`);
  lines.push((MAP_LIGHTING[layout.lighting] || MAP_LIGHTING[DEFAULT_MAP_LIGHTING]).prompt, ...groundLegend(layout));
  const wallsUsed = [...new Set(Object.values(layout.walls))].filter((kind) => WALL_KINDS[kind]);
  if (wallsUsed.length) {
    lines.push(`Lines along the square edges: ${wallsUsed.map((kind) => SKETCH_STYLE.walls[kind]).join('; ')}. Keep every wall, door and opening exactly where the sketch draws it.`);
  }
  const buildings = buildingGroups(layout);
  if (buildings.length) {
    lines.push('Buildings (columns count from the left and rows from the top, starting at 1). Each is one building with its own roof; keep its outline and put each door and window exactly where listed:');
    buildings.slice(0, MAX_PROMPT_BUILDINGS).forEach((building) => lines.push(describeBuilding(building)));
    if (buildings.length > MAX_PROMPT_BUILDINGS) lines.push(`- …and ${buildings.length - MAX_PROMPT_BUILDINGS} more buildings as drawn in the sketch.`);
  }
  const objectsUsed = [...new Set(Object.values(layout.objects))].filter((id) => MAP_OBJECTS[id]);
  if (objectsUsed.length) {
    lines.push('Marked areas (paint the object, not the mark):');
    for (const id of objectsUsed) lines.push(`- ${objectSketchColor(id)} areas labeled "${MAP_OBJECTS[id].symbol}": ${MAP_OBJECTS[id].prompt}`);
    lines.push('Each outlined patch of touching marked squares is ONE feature filling its whole area (one rubble field, one stand of brush, one stack of crates), never separate piles per square. Paint each patch as exactly the object listed and nothing else (crates and barrels are never a cart or wagon):');
    // Every patch is listed: one left out has only its sketch color to say what it is. A full 32 x 32 map still fits.
    objectGroups(layout).forEach((group) => {
      const width = group.right - group.left + 1;
      const height = group.bottom - group.top + 1;
      const size = group.cells.length === 1 ? 'one square' : width * height === group.cells.length ? `one ${width} by ${height} area` : `one area of ${group.cells.length} squares`;
      lines.push(`- ${MAP_OBJECTS[group.id].label}: ${size}, ${span(group.left, group.right, 'column')}, ${span(group.top, group.bottom, 'row')}`);
    });
  }
  // Numbered as in the editor, so the DM's pin 3 is circle 3. Pins without a note aren't drawn or listed.
  const notes = layout.pins.map((pin, index) => ({ pin, number: index + 1 })).filter(({ pin }) => pin.note);
  if (notes.length) {
    lines.push('Numbered notes: a white circle with a number marks each spot below (columns count from the left and rows from the top, starting at 1).');
    notes.forEach(({ pin, number }) => {
      const x = Math.min(columns - 1, Math.floor(pin.x));
      const y = Math.min(rows - 1, Math.floor(pin.y));
      lines.push(`- Circle ${number}, column ${x + 1}, row ${y + 1} (${describeMapArea(layout, pin.x, pin.y)}; ${pinSurroundings(layout, buildings, x, y)}): ${pin.note}`);
    });
    lines.push('A note that asks for an object or feature (such as a well) goes on its circled square, at the size it needs, and nowhere else. A note that names a kind of place (a tavern, a shop, a smithy) is what the building next to its circle is: show it on that building (a hanging sign beside its door, fitting roof details) and keep the circled square open ground; if no building is listed beside its circle, show it as a small feature (a stall or sign) on the circled square. Paint what each note says, never the circle or its number.');
  }
  lines.push('Every object that gives cover is marked in the sketch. Squares with no mark stay open, walkable ground: add no carts, wagons, stalls, decks, tables, wells, planters, barrels or other props on them unless a note above asks for one there. Flat ground detail (cracks, puddles, leaves, worn paving) is fine.');
  lines.push('Remove every sketch color block, line style, letter, circle and number from the final art; they only describe the layout. Do not draw markers, pins, numbers or labels anywhere.');
  return lines.join('\n');
};

/**
 * What a layout means for movement: wall edges that stop it (walls and windows; doors and low walls don't), squares
 * that can't be entered (a solid block, lava, a chasm), and difficult-terrain squares (rubble, brush, mud, deep water,
 * the squares under a sticker).
 * Edge keys use the layout's "x,y,h|v" form. For a mover with a Swim Speed (`swims`), deep water is not difficult.
 */
export const movementObstacles = (layout, { swims = false } = {}) => {
  // Squares whose stamp or ground paint has the flag, each listed once.
  const squares = (flag) => [...new Set([
    ...Object.entries(layout?.objects || {}).filter(([, id]) => MAP_OBJECTS[id]?.[flag]).map(([key]) => key),
    ...Object.entries(layout?.terrain || {}).filter(([, id]) => TERRAIN[id]?.[flag] && !(flag === 'difficult' && swims && TERRAIN[id]?.swimming)).map(([key]) => key),
    ...(layout?.stickers || []).filter(({ kind }) => STICKERS[kind]?.[flag]).flatMap((sticker) => stickerSquares(sticker).map(({ x, y }) => cellKey(x, y))),
  ])].map((key) => { const [x, y] = key.split(',').map(Number); return { x, y }; });
  return {
    walls: Object.entries(layout?.walls || {}).filter(([, kind]) => WALL_KINDS[kind]?.blocksMovement).map(([key]) => key),
    blocked: squares('blocksMovement'),
    difficult: squares('difficult'),
  };
};

/**
 * Whether moving the squares `cells` one step by (dx, dy) crosses a wall edge in `edges` (a Set of "x,y,h|v" keys). A
 * straight step crosses the edge between the squares. A diagonal step passes a grid corner and is stopped only when
 * both ways around that corner cross a wall, so a creature slips past a wall's end but not through a wall line.
 */
export const stepCrossesWalls = (edges, cells, dx, dy) => {
  if (!edges?.size) return false;
  const vertical = (x, y) => edges.has(`${x},${y},v`);
  const horizontal = (x, y) => edges.has(`${x},${y},h`);
  return cells.some((cell) => {
    const X = dx > 0 ? cell.x + 1 : cell.x;
    const Y = dy > 0 ? cell.y + 1 : cell.y;
    if (dx && !dy) return vertical(X, cell.y);
    if (dy && !dx) return horizontal(cell.x, Y);
    // The four wall edges meeting at corner (X,Y): in this square's row and the target's row (vertical edges), and in
    // this square's column and the target's column (horizontal edges).
    const ownRow = vertical(X, dy > 0 ? Y - 1 : Y);
    const targetRow = vertical(X, dy > 0 ? Y : Y - 1);
    const ownColumn = horizontal(dx > 0 ? X - 1 : X, Y);
    const targetColumn = horizontal(dx > 0 ? X : X - 1, Y);
    return (ownRow || targetColumn) && (ownColumn || targetRow);
  });
};

/**
 * A map's movement obstacles from its drawn layout, or null when it has none or its grid differs from the map's
 * `columns` x `rows` (then its squares would not line up with the tokens).
 */
export const mapMovementObstacles = (map, columns, rows) => {
  if (!map?.layout || typeof map.layout !== 'object') return null;
  const layout = normalizeLayout(map.layout);
  return layout.columns === columns && layout.rows === rows ? movementObstacles(layout) : null;
};
